import { X, ChevronRight, MessageCircle, Phone } from 'lucide-react';
import { AppLink } from '@/shared/components/AppLink';
import { isPlpRoute } from '@/features/plp/utils/plpUrl';
import { useEffect, useState, useRef } from 'react';
import { usePopupHistory } from '@/shared/hooks/usePopupHistory';
import { useLocation } from 'react-router-dom';
import { Button } from '@/shared/widgets/button';
import { useMenu, type MenuItem, type MenuCategory, type MenuSubCategory } from '../hooks/useMenu';
import { useUserContext } from '@/shared/features/user-context/presentation/hooks/useUserContext';
import { StoreIcon } from '@/shared/icons/icons';
import { CUSTOMER_CARE_TEL } from '@/shared/constants/contact';
import {
  NAVIGATION_BAR_CLICKED,
  navigationBarClickedEventProperties,
} from '@/shared/features/analytics/utils/navigationBarClicked';

interface HamburgerMenuProps {
  isOpen: boolean;
  onClose: () => void;
  onOpenLogin?: (mode?: 'login' | 'signup') => void;
}

function getErrorMessage(error: unknown): string {
  if (!error) return 'Unknown error';
  if (typeof error === 'string') return error;
  if (error instanceof Error) return error.message || 'Unknown error';
  if (typeof error === 'object') {
    const obj = error as { message?: unknown; statusText?: unknown; data?: unknown };
    if (typeof obj.message === 'string' && obj.message.trim()) return obj.message;
    if (typeof obj.statusText === 'string' && obj.statusText.trim()) return obj.statusText;
    if (typeof obj.data === 'string' && obj.data.trim()) return obj.data;
    try {
      return JSON.stringify(error);
    } catch {
      return 'Unknown error';
    }
  }
  return String(error);
}

export default function HamburgerMenu({ isOpen, onClose, onOpenLogin }: HamburgerMenuProps) {
    // Add hamburger open state to browser history so back closes it
    usePopupHistory({ isOpen, onClose, stateKey: 'hamburgerMenu' });
  const { menuData, isLoading, error } = useMenu();
  const { userContext } = useUserContext();
  const [isClosing, setIsClosing] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);
  const openDropdownsRef = useRef<Set<HTMLDetailsElement>>(new Set());
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  const loginUrl = `/showLoginPage?redirectUrl=${encodeURIComponent(
    location.pathname + location.search
  )}`;

  // Extract profile header from menu data
  const profileItem = menuData.find((item: MenuItem) => item.view_type === 'strip' && item.title === 'profile');
  const menuItemsWithoutProfile = menuData.filter((item: MenuItem) => !(item.view_type === 'strip' && item.title === 'profile'));

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (isOpen || shouldRender) {
      // Save original overflow style
      const originalOverflow = document.body.style.overflow;
      // Prevent scrolling
      document.body.style.overflow = 'hidden';
      // Mark hamburger as open for global UI (e.g. hide chat widget on mobile)
      const root = document.documentElement;
      root.dataset.hamburgerOpen = 'true';
      
      return () => {
        // Restore original overflow when menu closes
        document.body.style.overflow = originalOverflow;
        delete root.dataset.hamburgerOpen;
      };
    }
  }, [isOpen, shouldRender]);

  // Handle opening and closing animations
  // Note: setState in effect is necessary here for animation timing
  useEffect(() => {
    if (isOpen) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- Required for animation state management
      setShouldRender(true);
       
      setIsClosing(false);
    } else if (shouldRender) {
      // Remove focus from any focused element inside the menu before closing
      const activeElement = document.activeElement as HTMLElement;
      if (activeElement && scrollContainerRef.current?.contains(activeElement)) {
        activeElement.blur();
      }
      
      // Start closing animation
       
      setIsClosing(true);
      // Wait for animation to complete before resetting state
      const timer = setTimeout(() => {
        setShouldRender(false);
        setIsClosing(false);
        // Reset dropdown refs when menu closes
        openDropdownsRef.current.clear();
      }, 300); // Match animation duration
      return () => clearTimeout(timer);
    }
  }, [isOpen, shouldRender]);

  // Handle closing animation
  const handleClose = () => {
    if (isOpen) {
      onClose();
    }
  };

  // Always render HTML structure for SSR - use CSS to control visibility
  // This ensures the HTML is present in the source for SEO/SSR purposes
  // During SSR, isOpen will be false, but the HTML structure will still be rendered
  // Keep visible during closing animation to allow smooth transition
  // Use shouldRender to ensure visibility stays consistent during animation
  const isVisible = shouldRender;

  // Render sub_sub_categories (the final level with images and links)
  const renderSubSubCategory = (
    subSubCategory: MenuSubCategory,
    menuRootTitle: string,
    categoryTitle: string,
    subCategoryTitle: string,
  ) => {
    const navClick = navigationBarClickedEventProperties({
      menuName: menuRootTitle,
      categoryName: categoryTitle,
      subcategoryName: subCategoryTitle,
      buttonName: subSubCategory.title,
    });
    if (subSubCategory.link) {
      return (
        <li key={subSubCategory.title}>
          <AppLink
            to={subSubCategory.link}
            onClick={handleClose}
            shouldRefresh={subSubCategory.shouldRefresh ?? !isPlpRoute(subSubCategory.link.split('?')[0])}
            className="flex items-center gap-3 py-2.5 px-4 transition-colors border-gray-100 pl-16 hover:bg-[#fbe9ed]"
            eventName={NAVIGATION_BAR_CLICKED}
            eventProperties={navClick}
          >
            {subSubCategory.image_url && (
              <img 
                src={subSubCategory.image_url} 
                alt={subSubCategory.title}
                loading="lazy"
                decoding="async"
                className="w-12 h-12 object-cover rounded flex-shrink-0"
              />
            )}
            <span className="text-sm text-gray-700 flex-1 h-10 content-center">{subSubCategory.title}</span>
          </AppLink>
        </li>
      );
    }
    
    return (
      <li
        key={subSubCategory.title}
        className="flex items-center gap-3 py-2.5 px-4 border-gray-100"
      >
        {subSubCategory.image_url && (
          <img 
            src={subSubCategory.image_url} 
            alt={subSubCategory.title}
            loading="lazy"
            decoding="async"
            className="w-12 h-12 object-cover rounded flex-shrink-0"
          />
        )}
        <span className="text-sm text-gray-700 flex-1 h-10 content-center">{subSubCategory.title}</span>
      </li>
    );
  };

  // Check if element1 is an ancestor of element2
  const isAncestor = (element1: HTMLElement, element2: HTMLElement): boolean => {
    let parent = element2.parentElement;
    while (parent) {
      if (parent === element1) return true;
      parent = parent.parentElement;
    }
    return false;
  };
      
  // Handle dropdown toggle - close sibling dropdowns and scroll to top
  const handleDropdownToggle = (detailsElement: HTMLDetailsElement) => {
    if (detailsElement.open) {
      // Add this dropdown to the set of open dropdowns
      openDropdownsRef.current.add(detailsElement);
      
      // Close other open dropdowns that are siblings (not parent/child)
      openDropdownsRef.current.forEach((openDropdown) => {
        if (openDropdown !== detailsElement && openDropdown.open) {
          // Check if they are siblings (same parent or at same level)
          // Don't close if one is a parent/ancestor of the other
          const isOpenParent = isAncestor(openDropdown, detailsElement);
          const isOpenChild = isAncestor(detailsElement, openDropdown);
          
          // Only close if they are siblings (not parent/child relationship)
          if (!isOpenParent && !isOpenChild) {
            openDropdown.open = false;
            openDropdownsRef.current.delete(openDropdown);
          }
        }
      });
      
      // Smooth scroll to top of the summary element
      const summary = detailsElement.querySelector('summary');
      if (summary && scrollContainerRef.current) {
        // Use setTimeout to ensure DOM is updated after dropdown opens
        setTimeout(() => {
          if (scrollContainerRef.current && summary) {
            const container = scrollContainerRef.current;
            const summaryRect = summary.getBoundingClientRect();
            const containerRect = container.getBoundingClientRect();
            const scrollTop = container.scrollTop;
            // Calculate target scroll position to bring summary to top of container
            const targetScroll = scrollTop + summaryRect.top - containerRect.top - 10; // 10px offset from top
            
            container.scrollTo({
              top: Math.max(0, targetScroll),
              behavior: 'smooth'
            });
          }
        }, 50); // Small delay to ensure dropdown content is rendered
      }
    } else {
      // Remove from set when closed
      openDropdownsRef.current.delete(detailsElement);
    }
  };

  // Render sub_categories (like "Rings", "Earrings" under "Women")
  const renderSubCategory = (
    subCategory: MenuSubCategory,
    forceRefresh: boolean | undefined,
    menuRootTitle: string,
    categoryTitle: string,
  ) => {
    const hasSubSubCategories = subCategory.sub_sub_categories && subCategory.sub_sub_categories.length > 0;

    if (hasSubSubCategories) {
      // Expandable sub-category
      return (
        <li key={subCategory.title}>
          <details 
            className="group border-gray-200"
            onToggle={(e) => handleDropdownToggle(e.currentTarget)}
          >
            <summary className="flex items-center justify-between py-4 px-4 text-sm font-medium text-gray-900 cursor-pointer transition-colors group-open:bg-[#fdf2f5] hover:bg-[#fbe9ed]">
                <span className='pl-8'>{subCategory.title}</span>
              <ChevronRight className="h-4 w-4 text-gray-500 transition-transform group-open:rotate-90" />
            </summary>
            <ul>
              {subCategory.sub_sub_categories?.map((subSub) =>
                renderSubSubCategory(subSub, menuRootTitle, categoryTitle, subCategory.title),
              )}
            </ul>
          </details>
        </li>
      );
    }

    // Link only if no sub_sub_categories and link is available
    if (!hasSubSubCategories && subCategory.link) {
      return (
        <li key={subCategory.title}>
          <AppLink
            to={subCategory.link}
            onClick={handleClose}
            shouldRefresh={forceRefresh ?? subCategory.shouldRefresh ?? !isPlpRoute(subCategory.link.split('?')[0])}
            className="flex items-center gap-3 py-3 px-4 text-sm font-medium text-gray-900 transition-colors border-gray-200 pl-12 hover:bg-[#fbe9ed]"
            eventName={NAVIGATION_BAR_CLICKED}
            eventProperties={navigationBarClickedEventProperties({
              menuName: menuRootTitle,
              categoryName: categoryTitle,
              buttonName: subCategory.title,
            })}
          >
            {subCategory.image_url && (
              <img 
                src={subCategory.image_url} 
                alt={subCategory.title}
                loading="lazy"
                decoding="async"
                className="w-12 h-12 object-cover rounded flex-shrink-0"
              />
            )}
            <span className="flex-1 h-10 content-center">{subCategory.title}</span>
          </AppLink>
        </li>
      );
    }
    
    // Plain text sub-category (no sub_sub_categories, no link)
    return (
      <li
        key={subCategory.title}
        className="py-3 px-4 text-sm font-medium text-gray-900 border-b border-gray-200"
      >
        {subCategory.title}
      </li>
    );
  };

  // Render categories (like "Watch Jewellery", "Women", "Men", "Offers" under "Shop For")
  const renderCategory = (category: MenuCategory, forceRefresh: boolean | undefined, menuRootTitle: string) => {
    const hasSubCategories = category.sub_categories && category.sub_categories.length > 0;
    const inheritRefresh = forceRefresh || category.shouldRefresh === true || category.title === 'Offers';

    if (hasSubCategories) {
      // Expandable category – has sub_categories, show dropdown
      return (
        <li key={category.title}>
          <details 
            className="group border-b border-gray-200"
            onToggle={(e) => handleDropdownToggle(e.currentTarget)}
          >
            <summary className="flex items-center justify-between py-4 px-4 text-sm font-semibold text-gray-900 cursor-pointer transition-colors group-open:bg-[#fdf2f5] hover:bg-[#fbe9ed]">
                <span className='pl-4' style={{ color: category.title_color }}>{category.title}</span>
              <ChevronRight className="h-4 w-4 text-gray-500 transition-transform group-open:rotate-90" />
            </summary>
            <ul style={{ backgroundColor: 'rgba(244,195,203,0.2)' }}>
              {category.sub_categories?.map((subCategory) =>
                renderSubCategory(subCategory, inheritRefresh || undefined, menuRootTitle, category.title),
              )}
            </ul>
          </details>
        </li>
      );
    }

    // Link only if no sub_categories and url/link is available (API may send url)
    // Match left padding to expandable: summary has px-4 + span has pl-4 = pl-8 total
    const categoryHref = category.url ?? category.link;
    if (!hasSubCategories && categoryHref) {
      return (
        <li key={category.title} className="border-b border-gray-200">
          <AppLink
            to={categoryHref}
            onClick={handleClose}
            shouldRefresh={inheritRefresh || (category.shouldRefresh ?? !isPlpRoute(categoryHref.split('?')[0]))}
            className="block py-3 pl-8 pr-4 text-sm font-semibold text-gray-900 hover:bg-[#fbe9ed] transition-colors"
            style={{ color: category.title_color }}
            eventName={NAVIGATION_BAR_CLICKED}
            eventProperties={navigationBarClickedEventProperties({
              menuName: menuRootTitle,
              buttonName: category.title,
            })}
          >
            {category.title}
          </AppLink>
        </li>
      );
    }
    
    return (
      <li
        key={category.title}
        className="py-3 pl-8 pr-4 text-sm font-semibold text-gray-900 border-b border-gray-200"
        style={{ color: category.title_color }}
      >
        {category.title}
      </li>
    );
  };

  const renderMenuItem = (item: MenuItem, index: number) => {
    switch (item.view_type) {
      case 'strip': {
        // Profile strip is now rendered as fixed header, skip it here
        if (item.title === 'profile') {
          return null;
        }

        // Strip-specific styling for Gold Mine / Gold Reserve rows
        const stripText = `${item.display_name ?? ''} ${item.title ?? ''}`.toLowerCase();
        const isGoldMine = stripText.includes('goldmine');
        const isGoldReserve = stripText.includes('goldreserve');
        const isLocateOurStore =
          item.title === 'locate_our_store' ||
          stripText.includes('locate our store') ||
          (item.link?.includes('/store.html') ?? false);
        
        if (item.title === 'contact') {
          // Remove contact menu from hamburger
          return null;
        }

        // Locate Our Store: match `sample.html` (background image + inner gradient strip)
        if (isLocateOurStore) {
          const bgUrl = item.strip_img;
          return (
            <li key={index} className="">
              <div
                className=""
                style={{
                  background: bgUrl ? `url(${bgUrl}) no-repeat center right` : undefined,
                }}
              >
                <AppLink
                  to={item.link || '/store.html'}
                  onClick={handleClose}
                  data-title="Locate Store Clicked"
                  aria-label={item.display_name || item.title || 'Locate Our Store'}
                  shouldRefresh={item.shouldRefresh ?? !isPlpRoute((item.link || '/store.html').split('?')[0])}
                  className="flex items-center text-[16px] font-semibold text-[#001e38] relative py-[10px] pr-10 pl-5 hover:opacity-95 transition-opacity"
                  style={{
                    background:
                      'linear-gradient(to right, rgb(213, 187, 141) 0px, rgb(213, 187, 141) 50%, rgba(251, 230, 230, 0) 100%)',
                  }}
                  eventName={NAVIGATION_BAR_CLICKED}
                  eventProperties={navigationBarClickedEventProperties({
                    menuName: item.display_name || item.title || 'Locate Our Store',
                    buttonName: item.display_name || item.title || 'Locate Our Store',
                  })}
                >
                  <span className="leading-[0] mr-[15px]">
                    <StoreIcon className="h-10 w-10" />
                  </span>
                  <span className="uppercase tracking-[0.05em]">{item.display_name || item.title}</span>
                </AppLink>
              </div>
            </li>
          );
        }
        
        // Other strips (Gold Mine, Gold Reserve, Locate Our Store, etc.)
        const stripRowClassName =
          isGoldMine || isGoldReserve
            ? 'flex items-center gap-3 my-2 py-[10px] px-4 pl-[20px] border-y border-[#fbe6e6] transition-opacity hover:opacity-90'
            : 'flex items-center gap-3 p-4 my-2 bg-[#fdf2f5] border-b border-gray-200 hover:bg-[#fbe9ed] transition-colors';

        const stripRowStyle: React.CSSProperties | undefined = isGoldMine
          ? {
              background:
                'linear-gradient(to right, rgb(251, 230, 230) 0px, rgb(251, 230, 230) 50%, rgba(251, 230, 230, 0) 100%)',
            }
          : isGoldReserve
            ? {
                background:
                  '-webkit-linear-gradient(left, rgba(197, 168, 83, 0.125) 0px, rgba(255, 255, 255, 0.25) 99.46%)',
              }
            : undefined;

        return (
          <li key={index}>
            <AppLink
              to={item.link || '#'}
              onClick={handleClose}
              shouldRefresh={item.shouldRefresh ?? !isPlpRoute((item.link || '').split('?')[0])}
              className={stripRowClassName}
              style={stripRowStyle}
              eventName={NAVIGATION_BAR_CLICKED}
              eventProperties={navigationBarClickedEventProperties({
                menuName: item.display_name || item.title,
                buttonName: item.display_name || item.title,
              })}
            >
            {item.strip_img && (
              <img 
                src={item.strip_img} 
                alt={item.display_name || item.title}
                loading="lazy"
                decoding="async"
                className="h-auto w-28 object-contain flex-shrink-0"
              />
            )}
              <div className="flex-1">
                <div className="font-semibold text-gray-900">{item.display_name || item.title}</div>
              </div>
              <ChevronRight className="h-5 w-5 text-gray-600" />
            </AppLink>
          </li>
        );
      }

      case 'dropdown': {
        // "Shop For" and "Jewellery" should be labels, not dropdowns
        const isLabelOnly = item.title === 'Shop For' || item.title === 'Jewellery';
        
        if (isLabelOnly) {
          // Render as label header with categories displayed directly below
          return (
            <li key={index} className="border-b border-gray-200">
              {/* Label header */}
              <div className="py-4 px-4 text-sm font-semibold text-gray-900 bg-white" style={{ color: item.title_color }}>
                {item.title}
              </div>
              {/* Render all categories directly (they will be expandable) */}
              <ul>
                {item.categories?.map((category) => renderCategory(category, item.shouldRefresh === true || undefined, item.title))}
              </ul>
            </li>
          );
        }
        
        // Other dropdowns remain expandable
        return (
          <li key={index} className="border-b border-gray-200">
            <details 
              className="group"
              onToggle={(e) => handleDropdownToggle(e.currentTarget)}
            >
              <summary className="flex items-center justify-between py-3 px-4 text-sm font-semibold text-gray-900 cursor-pointer hover:bg-[#fbe9ed] transition-colors group-open:bg-[#fdf2f5]">
                  <span style={{ color: item.title_color }}>{item.title}</span>
                <ChevronRight className="h-4 w-4 transition-transform group-open:rotate-90" />
              </summary>
              <ul style={{ backgroundColor: 'rgba(244,195,203,0.2)' }}>
                {item.categories?.map((category) => renderCategory(category, item.shouldRefresh === true || undefined, item.title))}
              </ul>
            </details>
          </li>
        );
      }

      case 'text':
        return (
          <li key={index}>
            <AppLink
              to={item.link || '#'}
              onClick={handleClose}
              shouldRefresh={item.shouldRefresh ?? !isPlpRoute((item.link || '').split('?')[0])}
              className="block py-4 px-4 text-sm text-gray-700 hover:bg-[#fbe9ed] transition-colors border-b border-gray-200"
              style={{ color: item.title_color }}
              eventName={NAVIGATION_BAR_CLICKED}
              eventProperties={navigationBarClickedEventProperties({
                menuName: item.title,
                buttonName: item.title,
              })}
            >
              {item.title}
            </AppLink>
          </li>
        );

      default:
        return null;
    }
  };

  // Always render the HTML structure for SSR - this ensures it's in the page source
  // The menu structure will be present in the DOM even when hidden
  // Using style-based hiding instead of classes to ensure React always includes it
  // Only set aria-hidden when menu is fully closed (not during closing animation)
  const isFullyHidden = !shouldRender;
  const avatarLetter = (userContext?.customerName?.trim()?.[0] || 'G').toUpperCase();
  const errorMessage = getErrorMessage(error);
  
  return (
    <div 
      className="fixed inset-0 z-[2100] overflow-hidden"
      aria-hidden={isFullyHidden}
      data-hamburger-menu="true"
      style={{
        visibility: isVisible ? 'visible' : 'hidden',
        pointerEvents: (isOpen && shouldRender) ? 'auto' : 'none', // Disable during closing animation
        display: 'block',
      }}
    >
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50"
        data-menu-backdrop="true"
        style={{ 
          animation: isClosing ? 'fadeOut 0.3s ease-out' : (shouldRender && isOpen ? 'fadeIn 0.3s ease-out' : 'none')
        }}
        onClick={handleClose}
      />
      
      {/* Menu Panel */}
      <div 
        className="absolute left-0 top-0 bottom-0 w-full bg-white shadow-xl flex flex-col"
        data-menu-panel="true"
        style={{
          animation: isClosing ? 'slideOutToLeft 0.3s ease-out' : (shouldRender && isOpen ? 'slideInFromLeft 0.3s ease-out' : 'none')
        }}
      >
        {/* Fixed Header - Profile */}
        <div className="flex-shrink-0 bg-[#001e38] text-white p-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            {profileItem ? (
              userContext?.isLoggedIn ? (
                <AppLink
                  to={userContext?.isLoggedIn ? '/account.html' : loginUrl}
                  onClick={handleClose}
                  shouldRefresh={true}
                  className="flex flex-1 items-center gap-3 text-white"
                  eventName={NAVIGATION_BAR_CLICKED}
                  eventProperties={navigationBarClickedEventProperties({
                    menuName: 'Account',
                    buttonName: userContext.userName || 'Account',
                  })}
                >
                  <div className="w-10 h-10 rounded-full bg-white text-[#1e3a8a] flex items-center justify-center font-bold">
                    {avatarLetter}
                  </div>
                  <div className="flex-1">
                    <div className="font-semibold">
                      {userContext.userName || 'Account'}
                    </div>
                    <div className="text-xs text-white/80">Tap to view account details</div>
                  </div>
                </AppLink>
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    handleClose();
                    onOpenLogin?.('login');
                  }}
                  className="flex flex-1 items-center gap-3 text-white text-left w-full"
                >
                  <div className="w-10 h-10 rounded-full bg-white text-[#1e3a8a] flex items-center justify-center font-bold">
                    {avatarLetter}
                  </div>
                  <div className="flex-1">
                    <div className="text-xs text-white/60 mb-1">Guest User</div>
                    <div className="font-semibold text-white">Tap to Login / Sign Up</div>
                  </div>
                </button>
              )
            ) : (
              <div className="flex-1">
                <div className="font-semibold">Menu</div>
              </div>
            )}
            <Button
              variant="ghost"
              size="icon"
              onClick={handleClose}
              className="h-8 w-8 text-white hover:bg-white/20"
            >
              <X className="h-5 w-5" />
            </Button>
          </div>
        </div>

        {/* Content - Scrollable */}
        <div ref={scrollContainerRef} className="flex-1 overflow-y-auto" data-menu-content="true">
          {isLoading && (
            <div className="p-4 text-center text-gray-500">Loading menu...</div>
          )}

          {error && (
            <div className="p-4 text-center text-red-500">
              <div>Error: {errorMessage}</div>
              <div className="text-xs mt-2 text-gray-500">Please check the console for more details.</div>
            </div>
          )}

          {!isLoading && !error && menuItemsWithoutProfile.length > 0 && (
            <ul className="list-none">
              {menuItemsWithoutProfile
                .map((item: MenuItem, index: number) => renderMenuItem(item, index))
                .filter((item: React.ReactNode) => item !== null)}
            </ul>
          )}
          
          {/* Show message when menu data is empty */}
          {!isLoading && !error && menuItemsWithoutProfile.length === 0 && (
            <div className="p-4 text-center text-gray-500">
              <div>No menu items available</div>
              <div className="text-xs mt-2">Menu data: {menuData.length} items found</div>
            </div>
          )}
        </div>

        {/* Bottom Bar - Fixed */}
        <div className="flex-shrink-0 bg-gray-100 border-t border-gray-200 p-4 flex items-center justify-around">
          <button
            type="button"
            aria-label="Chat"
            className="flex flex-col items-center gap-1 text-gray-600 hover:text-gray-900 transition-colors"
            onClick={() => {
              if (typeof window !== 'undefined') {
                window.dispatchEvent(new Event('freshchat:open'));
              }
              handleClose();
            }}
          >
            <MessageCircle className="h-6 w-6" />
            <span className="text-xs">Chat</span>
          </button>
          <a
            href={CUSTOMER_CARE_TEL}
            aria-label="Call customer care"
            className="flex flex-col items-center gap-1 text-gray-600 hover:text-gray-900 transition-colors"
            onClick={() => {
              handleClose();
            }}
          >
            <Phone className="h-6 w-6" />
            <span className="text-xs">Call</span>
          </a>
        </div>
      </div>
    </div>
  );
}


